import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useCurrentUser } from "@/lib/auth";
import {
  LAYOUT_FULL_WIDTH_CHANGE_EVENT,
  bootstrapLayoutPreferenceFromServer,
  readLayoutFullWidthLocal,
  saveLayoutFullWidth,
} from "@/lib/layoutPreference";

interface LayoutPreferenceContextValue {
  fullWidth: boolean;
  setFullWidth: (enabled: boolean) => void;
  loading: boolean;
}

const LayoutPreferenceContext = createContext<LayoutPreferenceContextValue | null>(null);

export function LayoutPreferenceProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const [fullWidth, setFullWidthState] = useState(() => readLayoutFullWidthLocal());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void bootstrapLayoutPreferenceFromServer()
      .then((enabled) => {
        if (cancelled) return;
        setFullWidthState(enabled);
      })
      .catch(() => {
        if (cancelled) return;
        setFullWidthState(readLayoutFullWidthLocal());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ fullWidth?: boolean }>).detail;
      if (typeof detail?.fullWidth === "boolean") {
        setFullWidthState(detail.fullWidth);
        return;
      }
      setFullWidthState(readLayoutFullWidthLocal());
    };

    window.addEventListener(LAYOUT_FULL_WIDTH_CHANGE_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(LAYOUT_FULL_WIDTH_CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const setFullWidth = useCallback((next: boolean) => {
    setFullWidthState(next);
    void saveLayoutFullWidth(next);
  }, []);

  const value = useMemo(
    () => ({ fullWidth, setFullWidth, loading }),
    [fullWidth, setFullWidth, loading],
  );

  return (
    <LayoutPreferenceContext.Provider value={value}>{children}</LayoutPreferenceContext.Provider>
  );
}

export function useLayoutPreference(): LayoutPreferenceContextValue {
  const ctx = useContext(LayoutPreferenceContext);
  if (!ctx) {
    return {
      fullWidth: readLayoutFullWidthLocal(),
      setFullWidth: (next) => {
        void saveLayoutFullWidth(next);
      },
      loading: false,
    };
  }
  return ctx;
}
